import { Route } from '@angular/router';
import {
  CULTURE_ROUTE_PARAM,
  FlowBaseUrlEnum,
  NOT_FOUND_PAGE,
} from '@constants/app.constants';
import { resetPasswordGuard } from '@guards/reset-password/reset-password.guard';
import { environment } from 'src/environments/environment';
import { cmsDynamicDataGuard } from './guards/data/cms-dynamic-data.guard';
import { cmsFlowDataGuard } from './guards/data/cms-flow-data.guard';
import { hasValidCultureRouteGuard } from './guards/has-valid-culture-route/has-valid-culture-route.guard';
import { loggedInUserGuard } from '@guards/logged-in-user/logged-in-user.guard';
import { chatbotVisibleGuard } from '@guards/chatbot-visible-route/chatbot-visible-route.guard';
import { getInStateBookingDataGuard } from '@guards/data/get-in-state-booking-data.guard';
import { runSerially } from '@guards/run-serially.guard';
import { clearPromoCodeSerchGuard } from '@guards/clear-promo-code-serch/clear-promo-code-serch.guard';
import { growthBookFeatureGuard } from '@guards/growthbook-feature/growthbook-feature.guard';
import { GrowthBookFeatureKeys } from '@models/growthbook.models';
import { BreadcrumbsComponent } from '@components/breadcrumbs/breadcrumbs.component';
import { BreadcrumbEnum } from '@models/breadcrumb.model';
import { SetupService } from '@services/setup-service/setup.service';
import { devModeGuard } from '@guards/env/dev-mode.guard';
import { CmsDynamicSubFlows, CmsKeys, ContentPageKeys, FeaturePageKeys } from "@constants/cms.constants";
import { cmsDataGuard } from "@guards/data/cms-data.guard";

const home_page = () =>
  import('./features/home/home.page').then((m) => m.HomePage);
const multicity_page = () =>
  import('./features/multicity/multicity.component').then((m) => m.MulticityComponent);
const flow_wrapper_routes = () =>
  import('./features/flow-wrapper/flow-wrapper.routes').then((m) => m.routes);
const account_routes = () =>
  import('./features/account/account.routes').then((m) => m.routes);
const not_found_page = () =>
  import('./features/not-found/not-found.page').then((m) => m.NotFoundPage);
const car_rental_page = () =>
  import('./features/car-rental/car-rental.component').then((m) => m.CarRentalComponent);
const trips_routes = () =>
  import('./features/trips/trips.routes').then((m) => m.routes);
const password_reset_page = () =>
  import('./features/password-reset/password-reset.page').then(
    (m) => m.PasswordResetPage,
  );
const uplift_page = () =>
  import('./features/uplift/uplift-page').then(
    (m) => m.UpliftPage,
  );
const style_guide_page = () =>
  import('./features/style-guide/style-guide.page').then(
    (m) => m.StyleGuidePage,
  );
const cms_dynamic_page = () =>
  import('./features/dynamic-pages/dynamic-pages-wrapper/dynamic-pages-wrapper.component').then(
    (m) => m.DynamicPagesWrapperComponent,
  );
const cms_dynamic_page_umbraco = () =>
  import('./features/dynamic-pages/dynamic-pages.component').then(
    (m) => m.DynamicPagesComponent,
  );
const theme_page = () =>
  import('./ui/theme/theme.page').then(
    (m) => m.ThemePageComponent
  );
const setup_page = () =>
  import('./features/setup/setup.page').then(
    (m) => m.SetupPage,
  );

const cultureChildRoutes: Route[] = [
  {
    path: `${FlowBaseUrlEnum.BOOK}`,
    loadChildren: flow_wrapper_routes,
    canActivate: [cmsFlowDataGuard, chatbotVisibleGuard],
  },
  {
    path: `${FlowBaseUrlEnum.CANCEL}`,
    loadChildren: flow_wrapper_routes,
    canActivate: [cmsFlowDataGuard, chatbotVisibleGuard],
  },
  {
    path: `${FlowBaseUrlEnum.CHECK_IN}`,
    loadChildren: flow_wrapper_routes,
    canActivate: [cmsFlowDataGuard, chatbotVisibleGuard],
    data: {
      umbracoFeatures: [
        FeaturePageKeys.ClarifyingFinalStepOCI
      ]
    }
  },
  {
    path: `${FlowBaseUrlEnum.MODIFY}`,
    loadChildren: flow_wrapper_routes,
    canActivate: [cmsFlowDataGuard, chatbotVisibleGuard],
  },
  {
    path: `${FlowBaseUrlEnum.PAY}`,
    loadChildren: flow_wrapper_routes,
    canActivate: [cmsFlowDataGuard, chatbotVisibleGuard],
  },
  {
    path: `${FlowBaseUrlEnum.TRIPS}`,
    loadChildren: trips_routes,
    canActivate: [cmsFlowDataGuard, chatbotVisibleGuard],
    data: {
      [BreadcrumbsComponent.ROUTE_DATA_BREADCRUMB]: BreadcrumbEnum.TRIPS,
      additionalDynamicCmsKeys: [{ flowKey: CmsKeys.Confirmation }],
      umbracoContentPages: [ContentPageKeys.CheckInMessageBox],
      umbracoFeatures: [
        FeaturePageKeys.SkybidMMBHomePageV1,
        FeaturePageKeys.SkybidMMBHomePageV2,
        FeaturePageKeys.SkybidMMBHomePageV3,
        FeaturePageKeys.SkybidMMBHomePageV4,
        FeaturePageKeys.SkybidMMBHomePageV6,
        FeaturePageKeys.AppDownloadBanner,
        FeaturePageKeys.YourTripsImprovement
      ]
    }
  },
  {
    path: `${FlowBaseUrlEnum.UPGRADE}`,
    loadChildren: flow_wrapper_routes,
    canActivate: [cmsFlowDataGuard, chatbotVisibleGuard],
    data: { umbracoContentPages: [ContentPageKeys.Skybid, ContentPageKeys.SkybidPayment], umbracoFeatures: [FeaturePageKeys.UpgradeNotAvailable] }
  },
  {
    path: `${FlowBaseUrlEnum.ACCOUNT}`,
    loadChildren: account_routes,
    canActivate: [cmsFlowDataGuard, loggedInUserGuard, chatbotVisibleGuard],
  },
  {
    path: `${FlowBaseUrlEnum.MULTICITY}`,
    loadComponent: multicity_page,
    canActivate: [
      growthBookFeatureGuard,
      chatbotVisibleGuard,
      clearPromoCodeSerchGuard,
    ],
    data: {
      growthBookFeature: GrowthBookFeatureKeys.FEAT_US1077_MULTICITY,
      allowedValues: ['b']
    }
  },
  {
    path: 'password-reset',
    loadComponent: password_reset_page,
    canActivate: [
      getInStateBookingDataGuard,
      resetPasswordGuard,
      chatbotVisibleGuard,
    ],
  },
  {
    path: `${FlowBaseUrlEnum.CAR_RENTAL}`,
    loadComponent: car_rental_page,
    canActivate: [
      cmsFlowDataGuard,
      chatbotVisibleGuard,
      growthBookFeatureGuard,
    ],
    data: {
      growthBookFeature: 'feat-001893-car-rental-header-popup-deeplink',
      allowedValues: ['b']
    }
  },
  {
    path: `${NOT_FOUND_PAGE}`,
    loadComponent: not_found_page,
    canActivate: [
      getInStateBookingDataGuard,
      cmsFlowDataGuard,
      chatbotVisibleGuard,
    ],
  },
  {
    path: `${FlowBaseUrlEnum.UPLIFT}`,
    redirectTo: `${FlowBaseUrlEnum.FLEXPAY}`,
  },
  {
    path: `${FlowBaseUrlEnum.FLEXPAY}`,
    loadComponent: uplift_page,
  },
  {
    path: `${FlowBaseUrlEnum.THEME}`,
    loadComponent: theme_page,
    canActivate: [
      devModeGuard
    ],
  },
  {
    path: '',
    pathMatch: 'full',
    loadComponent: home_page,
    canActivate: [
      chatbotVisibleGuard,
      clearPromoCodeSerchGuard,
    ],
  },
];

if (!environment.production) {
  cultureChildRoutes.push({
    path: 'style-guide',
    loadComponent: style_guide_page,
  });
}

if (window.location.host.match(SetupService.setupPageDomains)) {
  cultureChildRoutes.push({
    path: 'setup',
    loadComponent: setup_page,
  });
}

cultureChildRoutes.push({
  path: '**',
  canActivate: [
    cmsDataGuard,
    cmsDynamicDataGuard,
    getInStateBookingDataGuard,
    chatbotVisibleGuard,
  ],
  loadComponent: cms_dynamic_page,
  data: {umbracoContentPages: [ContentPageKeys.Deals]}

});

export const getRoutes = (): Route[] => {
  const mainRoutes = [
    {
      path: `:${CULTURE_ROUTE_PARAM}`,
      children: cultureChildRoutes,
      canActivate: [
        runSerially([hasValidCultureRouteGuard, cmsFlowDataGuard]),
        chatbotVisibleGuard,
      ],
      data: {
        [BreadcrumbsComponent.ROUTE_DATA_BREADCRUMB]: BreadcrumbEnum.HOME,
        // TODO: Translations needed for feat-001996-destinations-map-redesign
        additionalDynamicCmsKeys: [{ flowKey: CmsKeys.Experience, subFlowKey: CmsDynamicSubFlows.WhereWeFly }],
        umbracoContentPages: [ContentPageKeys.AnnounceBanners, ContentPageKeys.FlashSales],
        umbracoDynamicPagesConfig: true,
        umbracoFeatures: [FeaturePageKeys.NavbarCopy]
      }
    },
    {
      path: '',
      pathMatch: 'full',
      loadComponent: home_page,
      canActivate: [
        runSerially([hasValidCultureRouteGuard, cmsFlowDataGuard]),
        getInStateBookingDataGuard,
        chatbotVisibleGuard,
      ],
    },
  ] as Route[];

  if (!environment.production) {
    mainRoutes.push({
      path: 'style-guide',
      loadComponent: style_guide_page,
    });
  }

  mainRoutes.push({
    path: '**',
    canActivate: [
      cmsDataGuard,
      cmsDynamicDataGuard,
      getInStateBookingDataGuard,
      chatbotVisibleGuard,
    ],
    loadComponent: cms_dynamic_page,
  });

  return mainRoutes;
};
